import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { legalDocumentsApi } from '@/lib/pillarApi';
import { runInBackground } from '@/lib/utils';
import { createComponentLogger } from '@/lib/logging/frontend-logger';
import type {
    GetLegalDocumentResponse,
    LegalDocumentListItem,
    ListPendingRequiredLegalDocumentsResponse,
} from '@pillar-portal/api-types';
import { buildLegalDocumentIframeSrcDoc } from './legal-document-rendering';

const ReturnToQuerySchema = z.object({
    returnTo: z.string().optional(),
});

export function LegalWorkflowPage() {
    const log = createComponentLogger('LegalWorkflowPage');
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();

    const returnTo: string | null = useMemo(() => {
        const queryParams: URLSearchParams = new URLSearchParams(location.search);
        const returnToValue: string | null = queryParams.get('returnTo');
        const parseResult = ReturnToQuerySchema.safeParse({ returnTo: returnToValue ?? undefined });
        if (!parseResult.success) {
            return null;
        }
        return parseResult.data.returnTo ?? null;
    }, [location.search]);

    const pendingQuery = useQuery<ListPendingRequiredLegalDocumentsResponse>({
        queryKey: ['pendingRequiredLegalDocuments'],
        queryFn: async () => {
            return await legalDocumentsApi.listPendingRequiredLegalDocuments();
        },
        staleTime: 0,
        refetchOnWindowFocus: false,
        refetchOnMount: true,
    });

    const pendingDocs: ReadonlyArray<LegalDocumentListItem> = pendingQuery.data?.legal_documents ?? [];

    const [selectedLegalDocumentUuid, setSelectedLegalDocumentUuid] = useState<string | null>(null);
    const [hasReachedBottom, setHasReachedBottom] = useState<boolean>(false);
    const [hasViewedLongEnough, setHasViewedLongEnough] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const selectedLegalDocumentUuidRef = useRef<string | null>(null);
    const iframeRef = useRef<HTMLIFrameElement | null>(null);
    const iframeScrollCleanupRef = useRef<(() => void) | null>(null);

    useEffect(() => {
        // Pick first doc automatically.
        if (selectedLegalDocumentUuid === null && pendingDocs.length > 0) {
            setSelectedLegalDocumentUuid(pendingDocs[0]!.legal_document_uuid);
        }
        if (pendingDocs.length === 0 && pendingQuery.isFetched && !pendingQuery.isFetching) {
            const destination: string = returnTo ?? '/dashboard';
            runInBackground(navigate(destination, { replace: true }), (err: unknown) => {
                log.error('Navigation failed', err);
            });
        }
    }, [
        navigate,
        log,
        pendingDocs,
        pendingQuery.isFetched,
        pendingQuery.isFetching,
        returnTo,
        selectedLegalDocumentUuid,
    ]);

    useEffect(() => {
        setHasReachedBottom(false);
        setHasViewedLongEnough(false);
        selectedLegalDocumentUuidRef.current = selectedLegalDocumentUuid;
        setErrorMessage(null);
        if (selectedLegalDocumentUuid === null) {
            return;
        }
        const timerId: number = window.setTimeout(() => {
            setHasViewedLongEnough(true);
        }, 1500);
        return () => {
            window.clearTimeout(timerId);
        };
    }, [selectedLegalDocumentUuid]);

    const selectedDocQuery = useQuery<GetLegalDocumentResponse | null>({
        queryKey: ['legalDocument', selectedLegalDocumentUuid],
        queryFn: async () => {
            if (selectedLegalDocumentUuid === null) {
                return null;
            }
            return await legalDocumentsApi.getLegalDocument(selectedLegalDocumentUuid);
        },
        enabled: selectedLegalDocumentUuid !== null,
        staleTime: 0,
    });

    const onIframeLoad = useCallback(() => {
        const iframe: HTMLIFrameElement | null = iframeRef.current;
        const currentSelectedUuid: string | null = selectedLegalDocumentUuidRef.current;
        if (iframe === null || currentSelectedUuid === null) {
            return;
        }

        // Ensure we don't leak listeners if the iframe reloads.
        if (iframeScrollCleanupRef.current !== null) {
            iframeScrollCleanupRef.current();
            iframeScrollCleanupRef.current = null;
        }

        const frameDoc: Document | null = iframe.contentDocument;
        const frameWin: Window | null = iframe.contentWindow;
        if (frameDoc === null || frameWin === null) {
            log.error('Unable to access iframe document/window (sandbox/CSP?)', {
                hasDocument: frameDoc !== null,
                hasWindow: frameWin !== null,
            });
            setErrorMessage('Unable to read legal document content. Please reload.');
            return;
        }

        const onScroll = () => {
            // Ignore events for stale iframes after switching documents.
            const stillSelectedUuid: string | null = selectedLegalDocumentUuidRef.current;
            if (stillSelectedUuid === null || stillSelectedUuid !== currentSelectedUuid) {
                return;
            }

            const root: HTMLElement = frameDoc.documentElement;
            // Threshold (1px) avoids high-DPI rounding issues.
            const delta: number = Math.abs(root.scrollHeight - root.clientHeight - root.scrollTop);
            const isAtBottom: boolean = root.scrollHeight <= root.clientHeight + 1 || delta < 1;
            if (isAtBottom) {
                setHasReachedBottom(true);
            }
        };

        frameWin.addEventListener('scroll', onScroll, { passive: true });
        iframeScrollCleanupRef.current = () => {
            frameWin.removeEventListener('scroll', onScroll);
        };

        // If the document is short (no scrolling), unlock immediately.
        onScroll();
    }, [log]);

    useEffect(() => {
        return () => {
            if (iframeScrollCleanupRef.current !== null) {
                iframeScrollCleanupRef.current();
                iframeScrollCleanupRef.current = null;
            }
        };
    }, []);

    const acceptMutation = useMutation({
        mutationFn: async (legalDocumentUuid: string) => {
            await legalDocumentsApi.acceptLegalDocument(legalDocumentUuid, {});
        },
        onSuccess: async () => {
            setErrorMessage(null);
            await queryClient.invalidateQueries({ queryKey: ['pendingRequiredLegalDocuments'] });
            await queryClient.invalidateQueries({ queryKey: ['legalDocument'] });
        },
        onError: (err) => {
            setErrorMessage(err instanceof Error ? err.message : 'Accept failed');
        },
    });

    const selectedDoc = selectedDocQuery.data?.legal_document ?? null;
    const canAccept: boolean =
        selectedLegalDocumentUuid !== null &&
        selectedDoc !== null &&
        hasReachedBottom &&
        hasViewedLongEnough &&
        !acceptMutation.isPending;

    const srcDoc: string | null =
        selectedDoc === null
            ? null
            : buildLegalDocumentIframeSrcDoc({
                  legalDocumentUuid: selectedDoc.legal_document_uuid,
                  legalDocumentContentType: selectedDoc.legal_document_content_type,
                  legalDocumentText: selectedDoc.legal_document_text,
              });

    return (
        <div className="p-6 max-w-6xl mx-auto">
            <h1 className="text-2xl font-bold mb-2">Legal Acceptance Required</h1>
            <p className="text-sm text-gray-600 mb-4">
                You must review and accept all required documents before using the platform.
            </p>

            {errorMessage && (
                <Card className="mb-4 border-red-300">
                    <CardHeader>
                        <CardTitle className="text-red-700">Error</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-sm">{errorMessage}</p>
                    </CardContent>
                </Card>
            )}

            <div className="grid grid-cols-12 gap-4">
                <div className="col-span-4">
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">Documents to accept</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2">
                            {pendingDocs.map((doc) => {
                                const isSelected: boolean =
                                    selectedLegalDocumentUuid ===
                                    doc.legal_document_uuid;
                                return (
                                    <button
                                        key={doc.legal_document_uuid}
                                        className={`w-full text-left border rounded p-2 text-sm ${
                                            isSelected ? 'border-blue-400 bg-blue-50' : 'hover:bg-gray-50'
                                        }`}
                                        onClick={() => {
                                            setSelectedLegalDocumentUuid(
                                                doc.legal_document_uuid
                                            );
                                        }}
                                    >
                                        <div className="font-medium">
                                            {doc.legal_document_title} ({doc.legal_document_version})
                                        </div>
                                        <div className="text-xs text-gray-600">
                                            In force since: {new Date(doc.in_force_since).toLocaleDateString()}
                                        </div>
                                    </button>
                                );
                            })}
                        </CardContent>
                    </Card>
                </div>

                <div className="col-span-8">
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between">
                            <CardTitle className="text-base">Review</CardTitle>
                            <Button
                                disabled={!canAccept}
                                onClick={() => {
                                    if (selectedLegalDocumentUuid === null) {
                                        setErrorMessage('No document selected');
                                        return;
                                    }
                                    acceptMutation.mutate(selectedLegalDocumentUuid);
                                }}
                            >
                                Accept
                            </Button>
                        </CardHeader>
                        <CardContent>
                            {selectedDocQuery.isLoading || pendingQuery.isLoading ? (
                                <p>Loading...</p>
                            ) : srcDoc ? (
                                <div className="space-y-2">
                                    <iframe
                                        title="Legal Document"
                                        sandbox="allow-scripts allow-same-origin"
                                        className="w-full h-[70vh] border rounded"
                                        srcDoc={srcDoc}
                                        ref={iframeRef}
                                        onLoad={onIframeLoad}
                                    />
                                    <div className="text-xs text-gray-600">
                                        Read to bottom: {String(hasReachedBottom)}; viewed long enough:{' '}
                                        {String(hasViewedLongEnough)}
                                    </div>
                                </div>
                            ) : (
                                <p>Select a document.</p>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
